// M005-F0053 (M005-D005 §6) — Studio tab view model.
//
// Pure, unit-testable heart of the org-wide Studio list. The grouping
// itself is NOT reimplemented here: it reuses the richer series grouping
// L1 ported into this directory (bookingsGrouping.js — the
// expandOccurrences/from-until/per-date-exception-overlay path), applied to
// the org-wide set exactly as §6.0 requires, replacing the dead
// AdminBookings component's dedupe-and-count presentation so every page
// that shows a booking series agrees on what a "booking" is (WEB-BKHUB-003).
//
// Two things this module owns beyond the grouping:
//   - the Studio ordering: everything is shown (the old component's
//     default filtered the list DOWN to pending only), with any
//     pending/pending_approval card floated first, then by start time;
//   - the filter -> query-param translation. Filters are wired to the real
//     GET /api/bookings query parameters (org/room/user/status/start/end
//     — verified in studioaeye-backend src/routes/booking.routes.ts,
//     `room`, `status`, `start`, `end`, `user` are all server-side
//     options), never client-only filtering of an unbounded fetch.
import { groupMyBookings } from './bookingsGrouping';
import {
  getStatusBadgeClass, getStatusText, resolveDisplayStatus,
} from './bookingStatus';

// BUG-010 caps GET /api/bookings at limit=100 (MAX_PAGINATION_LIMIT); an
// org-wide admin view wants the whole page, so ask for the cap explicitly
// rather than silently receiving the 50-row default. The route pages with
// `offset` (default 0, bounded — booking.routes.ts), so a larger org is
// reachable page by page via Load more — never silently truncated at the
// cap (!351 review, finding 1).
export const STUDIO_BOOKINGS_LIMIT = 100;

// The route answers { success, data, meta: { total, limit, offset } } —
// read the pagination meta out of an axios response so the tab can show
// "showing N of M" and offer Load more only when M is genuinely larger.
// A response without meta (older shape, unit fixtures) simply has no
// known total: no indicator, no Load more, list as fetched.
export const studioBookingsMeta = (response) => {
  const meta = response?.data?.meta;
  if (!meta || typeof meta.total !== 'number') return null;
  return meta;
};

// The requester display chain shared with the Calendar tab's requesterOf
// (bookingsCalendarModel.js): a guest booking has no user_name but does
// carry external_organizer_email — show that instead of a bare 'Unknown'
// (!351 review, finding 2). M004-F0678 (WebUI #81): the last resort is the
// sentence "Unknown requester" — never a bare id, never the string "null"
// (the production "Requested by: User #null" defect).
const printable = (value) => (
  typeof value === 'string' && value.trim() && value.trim().toLowerCase() !== 'null'
    ? value.trim()
    : ''
);
export const requesterNameOf = (booking) => (
  printable(booking?.user_name) || printable(booking?.user_email)
  || printable(booking?.external_organizer_email) || 'Unknown requester'
);

export const STUDIO_STATUS_ALL = 'all';

// The status query values GET /api/bookings?status= already accepts.
export const STUDIO_STATUS_FILTERS = [
  { value: STUDIO_STATUS_ALL, label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

// A card is "waiting for an admin decision" when its main booking row
// resolves to a pending-family display status. resolveDisplayStatus (the
// bookingStatus.js dual-status rule) decides — never a raw `status` read,
// so a `status='approved'` row whose approval_status still says pending
// keeps floating at the top until it is genuinely decided.
export const isPendingDisplayStatus = (statusKey) => (
  statusKey === 'pending' || statusKey === 'pending_approval'
);

// §6: pending first, then by start time. `sortStart` is the Date the
// grouping already computed per card (a series' next occurrence, a
// single's own start), so an ongoing series sorts by when it actually
// next happens, not by its (possibly ancient) anchor date. The pending
// flag is the PRIMARY key — a whole-array .sort by start time alone would
// silently destroy the float-to-top.
export const sortStudioCards = (cards) => [...cards].sort((a, b) => {
  if (Boolean(a.isPending) !== Boolean(b.isPending)) return a.isPending ? -1 : 1;
  return a.sortStart - b.sortStart;
});

// Turns the flat GET /api/bookings org-wide response into ONE card per
// non-recurring booking and ONE card per recurring series (never one row
// per occurrence), each carrying the person/title/status the Studio row
// renders and the pending-first ordering above.
export const buildStudioCards = (bookings, now = new Date()) => {
  const { upcoming, past } = groupMyBookings(bookings, now);
  const cards = [...upcoming, ...past].map((card) => {
    const mainBooking = card.kind === 'series' ? card.representative : card.booking;
    const resolved = resolveDisplayStatus(mainBooking);
    return {
      ...card,
      mainBooking,
      title: mainBooking.title || 'Untitled booking',
      personName: requesterNameOf(mainBooking),
      statusKey: resolved.key,
      statusText: getStatusText(mainBooking),
      statusBadgeClass: getStatusBadgeClass(mainBooking),
      isPending: isPendingDisplayStatus(resolved.key),
    };
  });
  return sortStudioCards(cards);
};

// Filter state -> the exact query parameters GET /api/bookings documents.
// `status: 'all'` (and empty filters) are omitted, so the default view
// really is EVERYTHING. The to-date is translated to the route's
// `end_time <= ?` predicate as to+1day so the admin's inclusive "to" date
// keeps bookings that END on that day (a raw `<= YYYY-MM-DD` comparison
// would silently drop the whole day — every booking that day ends after
// 00:00).
const nextDay = (yyyyMmDd) => {
  const date = new Date(`${yyyyMmDd}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

export const studioBookingsQuery = ({
  orgId,
  status = STUDIO_STATUS_ALL,
  roomId = '',
  userId = '',
  startDate = '',
  endDate = '',
  offset = 0,
}) => ({
  org: orgId,
  ...(status && status !== STUDIO_STATUS_ALL ? { status } : {}),
  ...(roomId ? { room: roomId } : {}),
  ...(userId ? { user: userId } : {}),
  ...(startDate ? { start: startDate } : {}),
  ...(endDate ? { end: nextDay(endDate) || undefined } : {}),
  ...(offset ? { offset } : {}),
  limit: STUDIO_BOOKINGS_LIMIT,
});
